/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ChannelReference } from '../models/ChannelReference';
import type { ConnectionKey } from '../models/ConnectionKey';
import type { DeviceResponse } from '../models/DeviceResponse';
import type { HspState } from '../models/HspState';
import type { HsspSetupUrl } from '../models/HsspSetupUrl';
import type { PlaybackRate } from '../models/PlaybackRate';
import type { ServerTimeEstimate } from '../models/ServerTimeEstimate';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class HsspService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Get the current HSSP state of the device.
     * Get the current HSSP state of the device.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hssp">Handy Synchronized Script Protocol (HSSP)</a> for additional information about the HSSP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HSSP state.
     * @throws ApiError
     */
    public getHsspState(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/hssp/state',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Setup the HSSP protocol.
     * Setup the HSSP protocol.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hssp">Handy Synchronized Script Protocol (HSSP)</a> for additional information about the HSSP protocol and code samples.
     *
     * If you can't host your own scripts, you can use our script hosting service to get a temporary script download URL:
     *
     * See the [Hosting API](https://www.handyfeeling.com/api/hosting/v2/docs/) or the [Hosting API docs](https://ohdoki.notion.site/Hosting-API-v2-814e654381e74f2faebe2ebd908a878f)
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param requestBody HSSP setup
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HSSP state.
     * @throws ApiError
     */
    public hsspSetup(
        xConnectionKey: (ConnectionKey | ChannelReference),
        requestBody: (HsspSetupUrl | {
            /**
             * When enabled the server will send HSP state change notifications over SSE.
             */
            notify?: boolean;
        }),
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hssp/setup',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Start the HSSP playback.
     * Start the HSSP playback.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hssp">Handy Synchronized Script Protocol (HSSP)</a> for additional information about the HSSP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param requestBody HSSP play
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HSSP state.
     * @throws ApiError
     */
    public hsspPlay(
        xConnectionKey: (ConnectionKey | ChannelReference),
        requestBody: {
            /**
             * The start time in milliseconds.
             */
            start_time: number;
            server_time: ServerTimeEstimate;
            playback_rate?: PlaybackRate;
            /**
             * The loop state.
             */
            loop?: boolean;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hssp/play',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Stop the HSSP playback.
     * Stop the HSSP playback.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hssp">Handy Synchronized Script Protocol (HSSP)</a> for additional information about the HSSP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HSSP state.
     * @throws ApiError
     */
    public hsspStop(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hssp/stop',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Pause the HSSP playback.
     * Pause the HSSP playback.
     *
     * Pause will pause the playback, but keep the current position. A subsequent resume command will continue playback from the paused position or from the current 'live' script position/time, depending on the resume `pickUp` flag.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hsp">Handy Streaming Protocol (HSP)</a> for additional information about the HSP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @returns HspState HSSP state.
     * @throws ApiError
     */
    public hsspPause(
        xConnectionKey: ConnectionKey,
    ): CancelablePromise<HspState> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hssp/pause',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
        });
    }

    /**
     * Resume the HSSP playback.
     * Resume the HSSP playback.
     *
     * Depending on the `pickUp` parameter, resume will either continue from the paused position (`pickUp` = false) or jump to the current 'live' position/time of the script (`pickUp` = true).
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hsp">Handy Streaming Protocol (HSP)</a> for additional information about the HSP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param requestBody HSSP resume
     * @returns HspState HSSP state.
     * @throws ApiError
     */
    public hsspResume(
        xConnectionKey: ConnectionKey,
        requestBody: {
            /**
             * If `true`, the playback will resume from the current 'live' position of the stream. If `false`, it will resume from the paused position.
             */
            pickUp?: boolean;
        },
    ): CancelablePromise<HspState> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hssp/resume',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Adjust the stream synchronization.
     * Adjust the stream playtime using the provided current time sample from the external source and filter.
     *
     * This can improve the synchronization between the device and the external source when the current time samples have some variable inaccuracies.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hssp">Handy Synchronized Script Protocol (HSSP)</a> for additional information about the HSSP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param requestBody HSSP time
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HSP state.
     * @throws ApiError
     */
    public setHsspTime(
        xConnectionKey: (ConnectionKey | ChannelReference),
        requestBody: {
            /**
             * The time.
             */
            current_time: number;
            server_time: ServerTimeEstimate;
            /**
             * The filter to use when setting the time.
             */
            filter?: number;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hssp/synctime',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}
